'use client'

import { useEffect } from 'react'

/**
 * global-error is de laatste vanglaag en blijft bewust dependency-light (inline
 * styles, geen @streep/ui-import) zodat hij ook rendert als de UI-bundle stuk
 * is. Daarom hier een eigen, compacte ChunkLoadError-detectie i.p.v. de gedeelde
 * useReloadOnChunkError-hook die de route-boundaries gebruiken: een chunk-fout
 * (een client met de vorige build na een deploy) herstellen we met een harde
 * reload, met een sessionStorage-debounce tegen reload-loops. Houd de patronen
 * en de debounce-sleutel/-venster hieronder in sync met isChunkLoadError /
 * useReloadOnChunkError in @streep/ui.
 */
function isChunkLoadError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const name = (error as { name?: string }).name ?? ''
  const message = (error as { message?: string }).message ?? ''
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk [\w-]+ failed/i.test(message) ||
    /Loading CSS chunk/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /importing a module script failed/i.test(message)
  )
}

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    if (isChunkLoadError(error)) {
      try {
        const key = 'streep:chunk-reload-at' // zelfde key als useReloadOnChunkError (bewust geïnlined)
        const last = Number(window.sessionStorage.getItem(key) ?? '0')
        if (Date.now() - last < 10_000) return // net al herladen → niet loopen
        window.sessionStorage.setItem(key, String(Date.now()))
      } catch {
        // geen sessionStorage beschikbaar - reload alsnog uitvoeren
      }
      window.location.reload()
      return
    }
    console.error(error)
  }, [error])

  return (
    <html lang="nl">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>Er ging iets mis - STREEP Backoffice</title>
      </head>
      <body
        style={{
          margin: 0,
          padding: 0,
          backgroundColor: '#0A0A0A',
          color: '#F0EDE8',
          fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          style={{
            textAlign: 'center',
            padding: '2rem',
            maxWidth: '480px',
            width: '100%',
          }}
        >
          {/* Icon */}
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              backgroundColor: '#2D2D2D',
              marginBottom: '1.5rem',
            }}
          >
            <span
              style={{
                fontSize: '1.25rem',
                fontWeight: '700',
                color: '#BEF264',
                lineHeight: 1,
              }}
            >
              !
            </span>
          </div>

          {/* Heading */}
          <h1
            style={{
              fontSize: '1.5rem',
              fontWeight: '600',
              color: '#F0EDE8',
              margin: '0 0 0.75rem 0',
            }}
          >
            Er ging iets mis
          </h1>

          {/* Description */}
          <p
            style={{
              fontSize: '0.9375rem',
              color: '#6B6B6B',
              margin: '0 0 1.5rem 0',
              lineHeight: '1.6',
            }}
          >
            Er is een onverwachte fout opgetreden. Probeer de pagina opnieuw te laden.
          </p>

          {/* Reset button */}
          <button
            onClick={reset}
            style={{
              backgroundColor: '#BEF264',
              color: '#0A0A0A',
              border: 'none',
              borderRadius: '6px',
              padding: '0.625rem 1.5rem',
              fontSize: '0.9375rem',
              fontWeight: '600',
              fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
              cursor: 'pointer',
            }}
          >
            Opnieuw proberen
          </button>

          {/* FO-364 - opaque fout-referentie (Next.js error-digest, staat ook
              in de server-log) zodat een gebruikersmelding herleidbaar is.
              Geen stack, geen PII. */}
          {error.digest ? (
            <p
              style={{
                fontSize: '0.75rem',
                color: '#6B6B6B',
                margin: '1rem 0 0 0',
                fontFamily: 'monospace',
                wordBreak: 'break-all',
              }}
            >
              ref: {error.digest}
            </p>
          ) : null}
        </div>
      </body>
    </html>
  )
}
